import { useCallback, useEffect, useMemo, useState } from 'react';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import { CREATED_AT_KEY, DESC } from 'common/constants/analyst-dashboards';
import { AttioSortDirection, AttioValuationFilter } from 'dashboard409a/common/constants/analyst-dashboard';
import { CURRENT_VALUATIONS_SLUG, OPPORTUNITIES_SLUG, PAST_VALUATIONS_SLUG } from 'dashboard409a/common/constants/urls';
import { OperationsModal } from 'dashboard409a/components/OperationsModal';
import { GenericWorkboard } from 'dashboard409a/components/ValuationsTable/common';
import { ValuationsTableBulkData } from 'dashboard409a/components/ValuationsTable/ValuationsTableBulkData';
import { useMultiColumnSort } from 'dashboard409a/hooks/useMultiColumnSort';
import { SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { useFindSlug } from 'dashboard409a/utilities';

const VALUATIONS_SORT_KEY = 'valuationsSort';

interface WorkboardDataTableProps {
  filters: AttioValuationFilter;
  orderBy?: string;
  order?: AttioSortDirection;
}

const WorkboardDataTable = ({
  filters,
  orderBy: initialOrderBy = CREATED_AT_KEY,
  order: initialOrder = DESC,
}: WorkboardDataTableProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const currentSlug = useFindSlug();

  // Determine sectionId based on the current route
  const sectionId = useMemo(() => {
    if (currentSlug === CURRENT_VALUATIONS_SLUG) {
      return CURRENT_VALUATIONS_SLUG;
    }
    if (currentSlug === PAST_VALUATIONS_SLUG) {
      return PAST_VALUATIONS_SLUG;
    }
    if (currentSlug === OPPORTUNITIES_SLUG) {
      return OPPORTUNITIES_SLUG;
    }
    return undefined;
  }, [currentSlug]);

  // Use shared multi-column sort hook - default sort is by created_at (creation date)
  const { sortCriteria, orderBy, order, handleRequestSort } = useMultiColumnSort({
    storageKey: VALUATIONS_SORT_KEY,
    defaultOrderBy: initialOrderBy,
    defaultOrder: initialOrder,
  });

  const [openModal, setOpenModal] = useState(false);
  const [selectedValuation, setSelectedValuation] = useState<SimplifiedAttioValuation | string | undefined>();

  // Parse query params
  const queryParams = useMemo(() => queryString.parse(location.search), [location.search]);
  const attioIdFromUrl = queryParams['attio-id'] as string | undefined;

  const handleOpenModal = useCallback((valuation: SimplifiedAttioValuation) => {
    setSelectedValuation(valuation);
    setOpenModal(true);
    
    // Update URL with attio-id query param
    const newQueryParams = { ...queryParams, 'attio-id': valuation.record_id };
    navigate({ search: queryString.stringify(newQueryParams) }, { replace: true });
  }, [navigate, queryParams]);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setSelectedValuation(undefined);

    // Remove attio-id from URL query params
    const newQueryParams = { ...queryParams };
    delete newQueryParams['attio-id'];
    navigate({ search: queryString.stringify(newQueryParams) }, { replace: true });
  }, [navigate, queryParams]);

  // Auto-open modal when attio-id is in URL and data is loaded
  useEffect(() => {
    if (attioIdFromUrl && !openModal) {
      setSelectedValuation(attioIdFromUrl);
      setOpenModal(true);
    }
  }, [attioIdFromUrl, openModal]);

  return (
    <GenericWorkboard>
      <ValuationsTableBulkData
        filters={filters}
        orderBy={orderBy}
        order={order}
        sortCriteria={sortCriteria}
        onRequestSort={handleRequestSort}
        onRowClick={handleOpenModal}
        sectionId={sectionId}
        pauseBatchLoading={openModal}
      />
      <OperationsModal
        open={openModal}
        onClose={handleCloseModal}
        data={selectedValuation}
        isLoading={false}
        isFromModels={false}
      />
    </GenericWorkboard>
  );
};

export default WorkboardDataTable;
